import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FileText, FileCheck, Receipt, Users, Package,
  UserSquare2, Wallet, BarChart3, Settings, Shield, LogOut, Menu, X, ChevronDown, RefreshCw, FileBarChart2, CreditCard, UsersRound, KeyRound, Wrench,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { WiseAceLogo } from "@/components/WiseAceLogo";
import { InstallPWA } from "@/components/InstallPWA";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, id: "dashboard" },
  { to: "/invoices", icon: FileText, label: "Invoices", id: "invoices" },
  { to: "/quotes", icon: FileCheck, label: "Quotes", id: "quotes" },
  { to: "/recurring", icon: RefreshCw, label: "Recurring", id: "recurring" },
  { to: "/payslips", icon: Receipt, label: "Payslips", id: "payslips" },
  { to: "/customers", icon: Users, label: "Customers", id: "customers" },
  { to: "/products", icon: Package, label: "Products", id: "products" },
  { to: "/employees", icon: UserSquare2, label: "Employees", id: "employees" },
  { to: "/payments", icon: Wallet, label: "Payments", id: "payments" },
  { to: "/reports", icon: BarChart3, label: "Reports", id: "reports" },
  { to: "/work-reports", icon: FileBarChart2, label: "Work Reports", id: "work-reports" },
  { to: "/plumbing", icon: Wrench, label: "Plumbing", id: "plumbing", perm: "plumbing.view" },
  { to: "/team", icon: UsersRound, label: "Team", id: "team", perm: "team.view" },
  { to: "/settings/api", icon: KeyRound, label: "API", id: "api", perm: "api.view" },
  { to: "/billing", icon: CreditCard, label: "Billing", id: "billing" },
  { to: "/settings", icon: Settings, label: "Settings", id: "settings" },
];

function NavItems({ onClick, isAdmin, hasPerm }) {
  const items = [...NAV].filter((n) => !n.perm || hasPerm(n.perm));
  if (isAdmin) items.push({ to: "/admin", icon: Shield, label: "Admin", id: "admin" });
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          data-testid={`nav-${n.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive
                ? "bg-white/10 text-white border-l-2 border-[#D4AF37]"
                : "text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
            }`
          }
        >
          <n.icon className="w-[18px] h-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const hasPerm = (perm) => user?.role === "admin" || user?.is_owner || (Array.isArray(user?.permissions) && user.permissions.includes(perm));

  if (user?.workspace_role === "field_employee") return <Navigate to="/field" replace />;
  if (user?.is_owner && user?.onboarding_completed === false) return <Navigate to="/onboarding" replace />;

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login");
  };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const Sidebar = (
    <div className="h-full flex flex-col py-5" style={{ background: "linear-gradient(180deg,#0B132B 0%,#1C2541 100%)" }}>
      <div className="px-5 pb-5 flex items-center justify-between">
        <WiseAceLogo light />
        <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} data-testid="close-sidebar">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <NavItems onClick={() => setOpen(false)} isAdmin={isAdmin} hasPerm={hasPerm} />
      </div>
      {user?.is_demo && (
        <div className="mx-3 mb-3 px-3 py-2 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold text-center" data-testid="demo-banner">
          DEMO ACCOUNT
        </div>
      )}
      <div className="px-3 pt-3 border-t border-white/10">
        <button
          onClick={doLogout}
          data-testid="sidebar-logout"
          className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-all"
        >
          <LogOut className="w-[18px] h-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* desktop sidebar */}
      <aside className="hidden lg:block w-[260px] fixed inset-y-0 left-0 z-30">{Sidebar}</aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[270px]">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 lg:ml-[260px] min-w-0">
        {/* top bar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-700" onClick={() => setOpen(true)} data-testid="open-sidebar">
              <Menu className="w-6 h-6" />
            </button>
            <div className="lg:hidden">
              <WiseAceLogo size={34} showText={false} />
            </div>
            <InstallPWA />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-all" data-testid="user-menu-trigger">
                <div className="w-9 h-9 rounded-full bg-[#0B132B] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-semibold text-[#0B132B] leading-tight">{user?.name}</div>
                  <div className="text-xs text-slate-500 leading-tight">{user?.role === "admin" ? "Administrator" : "Account"}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">Business Settings</DropdownMenuItem>
              {isAdmin && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin Panel</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="text-rose-600" data-testid="menu-logout">Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto wa-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
